<template>
  <StaffLayout section="Alert History (All Portals)" title="Alert History (All Portals)" hideHeader>
    <h1 class="py-3 mb-6 border-b flex">
      <div class="text-xl">
        <div class="font-bold">Alert History — {{ localPortal?.name ?? 'All Portals' }}</div>
      </div>
    </h1>

    <div class="flex flex-wrap gap-4 mb-4 items-end">
      <div>
        <label class="block text-sm font-medium mb-1">Start Date</label>
        <input type="date" v-model="startDate" class="border rounded px-2 py-1 text-sm" />
      </div>
      <div>
        <label class="block text-sm font-medium mb-1">End Date</label>
        <input type="date" v-model="endDate" class="border rounded px-2 py-1 text-sm" />
      </div>
      <div class="flex gap-2 items-center text-xs">
        <button class="text-blue-600 hover:underline" @click="setRange(30)">30 days</button>
        <button class="text-blue-600 hover:underline" @click="setRange(90)">90 days</button>
        <button class="text-blue-600 hover:underline" @click="setRange(182)">6 months</button>
        <button class="text-blue-600 hover:underline" @click="setRange(365)">12 months</button>
      </div>
    </div>

    <div class="mb-4">
      <label class="block text-sm font-medium mb-1">Filter by Portal</label>
      <div class="flex items-center gap-3">
        <PropertySelector v-model="localPortal" @selected="onPortalSelected" />
        <button
          v-if="selectedPortalId !== null"
          class="text-xs text-blue-600 hover:underline"
          @click="clearPortalFilter"
        >All Portals</button>
      </div>
    </div>

    <div class="mt-4">
      <Loading v-if="isLoading" />
      <div v-else>
        <div class="mt-4" v-if="sortedHistories?.length > 0">
          <!-- Summary metric cards: wrap with browser width -->
          <div class="flex flex-wrap gap-3 mb-4">
            <div
              v-for="metric in summaryMetrics"
              :key="metric.label"
              class="flex flex-col px-4 py-2 border rounded-lg bg-white shadow-sm min-w-[9rem]"
              :title="metric.title"
            >
              <span class="text-xs text-gray-500">{{ metric.label }}</span>
              <span class="text-2xl font-bold">{{ metric.value }}</span>
              <span v-if="metric.note" class="text-xs text-gray-400">{{ metric.note }}</span>
            </div>
          </div>
          <table class="table-auto w-full border-2 shadow text-sm">
            <thead class="font-sm font-bold">
              <tr>
                <td class="pl-3"></td>
                <td v-if="showPortalColumns" class="pl-3 cursor-pointer" @click="changeSort(SortColumn.Client)">
                  Client <UpDownArrow v-if="columnToSort === SortColumn.Client" :is-down="isSortingDown" />
                </td>
                <td v-if="showPortalColumns" class="pl-3 cursor-pointer" @click="changeSort(SortColumn.Portal)">
                  Portal <UpDownArrow v-if="columnToSort === SortColumn.Portal" :is-down="isSortingDown" />
                </td>
                <td class="pl-3 py-2 cursor-pointer" @click="changeSort(SortColumn.SentBy)">
                  Sent By <UpDownArrow v-if="columnToSort === SortColumn.SentBy" :is-down="isSortingDown" />
                </td>
                <td class="px-3 cursor-pointer" @click="changeSort(SortColumn.DateSent)">
                  Date Sent <UpDownArrow v-if="columnToSort === SortColumn.DateSent" :is-down="isSortingDown" />
                </td>
                <td class="px-3 cursor-pointer text-center" @click="changeSort(SortColumn.Count)">
                  Messages <UpDownArrow v-if="columnToSort === SortColumn.Count" :is-down="isSortingDown" />
                </td>
                <td
                  class="px-3 cursor-pointer whitespace-nowrap"
                  :title="`Message characters (estimated SMS cost: recipients x message parts x $${SMS_COST_PER_PART} per part)`"
                  @click="changeSort(SortColumn.Length)"
                >
                  Length <UpDownArrow v-if="columnToSort === SortColumn.Length" :is-down="isSortingDown" />
                </td>
                <td class="px-3">Message</td>
              </tr>
            </thead>
            <template v-for="(history, i) in sortedHistories" :key="i">
              <tr
                class="font-xs font-mono border-bottom-2 hover:border-sky-800 hover:bg-sky-300 cursor-pointer"
                :class="{ 'bg-stone-200': i % 2 === 0 }"
                @click="selectClick(history)"
              >
                <td class="pl-3">
                  <div class="px-2">
                    <IHeroiconsOutlineChevronUp
                      class="mx-auto"
                      :class="{
                        'transform rotate-180': history.alertHistoryId !== selectedHistoryId,
                      }"
                    />
                  </div>
                </td>
                <td v-if="showPortalColumns" class="pl-3">{{ history.clientName }}</td>
                <td v-if="showPortalColumns" class="pl-3 text-xs">
                  <span :title="history.portalPropertyName ?? undefined">{{ history.portalSiteCode }}</span>
                </td>
                <td class="pl-3">
                  <span :title="history.senderPersonLoginEmail || undefined">
                    {{ [history.senderPersonFirstName, history.senderPersonLastName].filter(Boolean).join(" ") || history.senderPersonLoginEmail }}
                  </span>
                </td>
                <td class="px-3 whitespace-nowrap">
                  <span>{{ formatDateTime(history.dateSent) }}</span>
                </td>
                <td class="px-3 text-center">
                  <span :title="`Email: ${history.emailCount ?? 0}  SMS: ${history.smsCount ?? 0}  Push: ${history.pushCount ?? 0}  People: ${history.peopleCount ?? 0}`">
                    {{ history.recipientCount }}
                  </span>
                </td>
                <td
                  class="px-3 whitespace-nowrap"
                  :title="`${smsRecipientCount(history.smsList)} SMS recipients x ${smsPartCount(history.messageText)} parts`"
                >
                  {{ formatLength(history) }}
                </td>
                <td class="px-3 max-w-xs">
                  <span
                    :title="history.messageText || undefined"
                    class="block whitespace-nowrap overflow-hidden text-ellipsis max-w-xs"
                  >{{ history.messageText }}</span>
                </td>
              </tr>

              <!-- Detail panel -->
              <template v-if="history.alertHistoryId === selectedHistoryId">
                <tr>
                  <td :colspan="showPortalColumns ? 8 : 6" class="px-8 py-0">
                    <!-- Message info panel: label/value boxes that wrap with browser width -->
                    <div class="flex flex-wrap gap-2 mt-1 mb-2 text-sm">
                      <div
                        v-for="stat in detailStats(history)"
                        :key="stat.label"
                        class="flex flex-col px-3 py-1 border rounded bg-white shadow-sm min-w-[8rem]"
                        :title="stat.title"
                      >
                        <span class="text-xs text-gray-500">{{ stat.label }}</span>
                        <span class="font-mono font-bold">{{ stat.value }}</span>
                      </div>
                    </div>

                    <!-- Message shown as a received text on a phone screen -->
                    <div class="phone-panel ml-4 max-w-sm border-2 border-gray-400 rounded-2xl bg-gray-50 shadow">
                      <div class="text-xs text-center text-gray-500 border-b border-gray-200 py-1">
                        {{ history.portalPropertyName ?? "Alert" }}
                      </div>
                      <div class="px-3 py-2">
                        <div class="phone-bubble">{{ history.messageText }}</div>
                        <div class="text-[10px] text-gray-400 mt-1 ml-1">
                          {{ formatDateTime(history.dateSent) }}
                        </div>
                      </div>
                      <!-- Double-byte (UCS-2) warning -->
                      <div
                        v-if="findNonGsmChars(history.messageText).length > 0"
                        class="mx-3 mb-3 px-3 py-2 text-xs text-orange-800 bg-orange-50 border border-orange-300 rounded-lg"
                      >
                        <div class="font-bold">Double-byte SMS encoding</div>
                        <div>
                          These characters forced UCS-2 encoding (67 chars per part instead of 153):
                          <span
                            v-for="ch in findNonGsmChars(history.messageText)"
                            :key="ch"
                            class="inline-block font-mono bg-white border border-orange-300 rounded px-1 mx-0.5"
                          >{{ describeChar(ch) }}</span>
                        </div>
                        <div class="mt-1">
                          Without them: {{ gsmPartCount(toGsm(history.messageText).text) }} parts
                          (est. ${{ gsmOnlyCost(history).toFixed(2) }}) vs.
                          {{ smsPartCount(history.messageText) }} parts
                          (est. ${{ estimatedCost(history).toFixed(2) }}).
                        </div>
                      </div>
                    </div>
                    <div class="mt-1 mb-1">
                      <a
                        href="#"
                        class="text-blue-600 hover:underline"
                        @click.prevent="navigateToDetail(history.alertHistoryId!)"
                        >More Detail</a
                      >
                    </div>
                  </td>
                </tr>
              </template>
              <!-- End Detail panel -->

            </template>
          </table>
        </div>
        <div v-else class="mb-2">
          <span>-- No Alert History found --</span>
        </div>
      </div>
    </div>
  </StaffLayout>
</template>

<script setup lang="ts">
import { useAlertStore, AlertHistoriesUiState } from "../../store/alertStore";
import { AlertHistoryAllDTO, PortalDTO } from "../../store/generatedFetchClient";
import StaffLayout from "../../layouts/StaffLayout.vue";
import PropertySelector from "../../components/PropertySelector.vue";
import { formatDateTime } from "../../helpers";
import {
  EMAIL_COST_PER_EMAIL,
  SMS_COST_PER_PART,
  estimateSmsCost,
  findNonGsmChars,
  gsmPartCount,
  smsCharCount,
  toGsm,
  smsPartCount,
  smsRecipientCount,
} from "../../helpers/gsm";
import { useBldgStore } from "../../store/bldgStore";

const router = useRouter();
const route = useRoute();

enum SortColumn {
  Client,
  Portal,
  SentBy,
  DateSent,
  Count,
  Length,
}

// EmailList is stored comma-separated, like SmsList
function emailRecipientCount(history: AlertHistoryAllDTO) {
  return (history.emailList ?? "").split(",").filter((e) => e.trim().length > 0).length;
}

// Stats shown in the expanded row's info panel
function detailStats(history: AlertHistoryAllDTO) {
  const smsRecipients = smsRecipientCount(history.smsList);
  const parts = smsPartCount(history.messageText);
  return [
    { label: "Message length", value: `${smsCharCount(history.messageText)} chars` },
    { label: "Message parts", value: parts },
    { label: "SMS recipients", value: smsRecipients },
    { label: "Email recipients", value: emailRecipientCount(history) },
    { label: "Push recipients", value: history.pushCount ?? 0 },
    {
      label: "Message SMS cost (est.)",
      value: `$${estimatedCost(history).toFixed(2)}`,
      title: `${smsRecipients} SMS recipients x ${parts} parts x $${SMS_COST_PER_PART} per part`,
    },
    {
      label: "Message email cost (est.)",
      value: formatCost(estimatedEmailCost(history)),
      title: `${emailRecipientCount(history)} email recipients x $${EMAIL_COST_PER_EMAIL} per email (SES pay-as-you-go)`,
    },
  ];
}

// Estimated cost had the message used GSM look-alikes (non-GSM chars swapped/removed)
function gsmOnlyCost(history: AlertHistoryAllDTO) {
  return estimateSmsCost(toGsm(history.messageText).text, smsRecipientCount(history.smsList));
}

// Show a char plus its code point, so invisible/look-alike chars are identifiable: "– U+2013"
function describeChar(ch: string) {
  const code = ch.codePointAt(0)?.toString(16).toUpperCase().padStart(4, "0");
  const visible = /\s/.test(ch) || /[​-‍⁠﻿]/.test(ch) ? "" : `${ch} `;
  return `${visible}U+${code}`;
}

function estimatedCost(history: AlertHistoryAllDTO) {
  return estimateSmsCost(history.messageText, smsRecipientCount(history.smsList));
}

function estimatedEmailCost(history: AlertHistoryAllDTO) {
  return emailRecipientCount(history) * EMAIL_COST_PER_EMAIL;
}

// Email costs are fractions of a cent: show 4 decimals below $0.01 so they don't read as $0.00
function formatCost(value: number) {
  return `$${value > 0 && value < 0.01 ? value.toFixed(4) : value.toFixed(2)}`;
}

// "603 ($1.23)": message chars + estimated SMS cost (recipients x parts x cost per part)
function formatLength(history: AlertHistoryAllDTO) {
  return `${smsCharCount(history.messageText)} ($${estimatedCost(history).toFixed(2)})`;
}

const alertStore = useAlertStore();
const bldgStore = useBldgStore();
const allHistories = computed(() => alertStore.allHistories);
const MAX_RESULTS = 1000;
const isLoading = ref(false);

const selectedPortalId = ref<number | null>(null);
const localPortal = ref<PortalDTO | null>(null);

function onPortalSelected(portal: PortalDTO | null) {
  selectedPortalId.value = portal?.portalId ?? null;
}

// Client/Portal columns are redundant when filtered to a single portal
const showPortalColumns = computed(() => selectedPortalId.value === null);

function clearPortalFilter() {
  selectedPortalId.value = null;
  localPortal.value = null;
}

function getDateString(daysAgo: number): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.toISOString().split("T")[0];
}

const startDate = ref<string>(getDateString(90));
const endDate = ref<string>(getDateString(0));

const loadedStart = ref<string>("");
const loadedEnd = ref<string>("");

function isSubsetOfLoaded(newStart: string, newEnd: string): boolean {
  if (!loadedStart.value || !loadedEnd.value) return false;
  return newStart >= loadedStart.value && newEnd <= loadedEnd.value;
}

watch([startDate, endDate], ([newStart, newEnd]) => {
  if (!newStart || !newEnd) return;
  if (isSubsetOfLoaded(newStart, newEnd)) return;
  loadData();
});

function setRange(days: number) {
  startDate.value = getDateString(days);
  endDate.value = getDateString(0);
}

const dateRangeDays = computed(() => {
  if (!startDate.value || !endDate.value) return 0;
  const ms = new Date(endDate.value).getTime() - new Date(startDate.value).getTime();
  return Math.round(ms / 86_400_000);
});

const columnToSort = ref<SortColumn>(SortColumn.DateSent);
const isSortingDown = ref(true);

const sortedHistories = computed(() => {
  let rows = allHistories.value?.slice() ?? [];
  if (selectedPortalId.value !== null) {
    rows = rows.filter((h) => h.portalId === selectedPortalId.value);
  }
  if (startDate.value) {
    const startMs = new Date(startDate.value + "T00:00:00").getTime();
    rows = rows.filter((h) => h.dateSent && new Date(h.dateSent).getTime() >= startMs);
  }
  if (endDate.value) {
    const endMs = new Date(endDate.value + "T23:59:59").getTime();
    rows = rows.filter((h) => h.dateSent && new Date(h.dateSent).getTime() <= endMs);
  }
  rows.sort((a, b) => {
    switch (columnToSort.value) {
      case SortColumn.Client:
        return (a.clientName ?? "").localeCompare(b.clientName ?? "");
      case SortColumn.Portal:
        return (a.portalSiteCode ?? "").localeCompare(b.portalSiteCode ?? "");
      case SortColumn.SentBy:
        return (a.senderPersonLoginEmail ?? "").localeCompare(b.senderPersonLoginEmail ?? "");
      case SortColumn.DateSent:
        return (a.dateSent ? new Date(a.dateSent).getTime() : 0) - (b.dateSent ? new Date(b.dateSent).getTime() : 0);
      case SortColumn.Count:
        return (a.recipientCount ?? 0) - (b.recipientCount ?? 0);
      case SortColumn.Length:
        return estimatedCost(a) - estimatedCost(b);
    }
  });
  if (isSortingDown.value) rows.reverse();
  return rows;
});

// Summary totals for the rows shown. A multi-building send saves one history row
// per building with identical message + recipient lists; count that send once.
// (The send endpoint rejects identical message + lists within 10 minutes, so a
// match inside that window is always the same send.)
const totals = computed(() => {
  const DUPLICATE_WINDOW_MS = 10 * 60 * 1000;
  const lastSeen = new Map<string, number>();
  const result = { sms: 0, email: 0, push: 0, cost: 0, emailCost: 0 };

  // Oldest first, ties by id: the first row of a multi-building send holds its
  // recipient details (push counts), so that is the row kept
  const byDate = sortedHistories.value
    .slice()
    .sort(
      (a, b) =>
        new Date(a.dateSent ?? 0).getTime() - new Date(b.dateSent ?? 0).getTime() ||
        (a.alertHistoryId ?? 0) - (b.alertHistoryId ?? 0)
    );

  for (const history of byDate) {
    const key = `${history.messageText}|${history.smsList}|${history.emailList}`;
    const sentMs = new Date(history.dateSent ?? 0).getTime();
    const previousMs = lastSeen.get(key);
    lastSeen.set(key, sentMs);
    if (previousMs !== undefined && sentMs - previousMs <= DUPLICATE_WINDOW_MS) continue;

    result.sms += smsRecipientCount(history.smsList);
    result.email += emailRecipientCount(history);
    result.push += history.pushCount ?? 0;
    result.cost += estimatedCost(history);
    result.emailCost += estimatedEmailCost(history);
  }
  return result;
});

const summaryMetrics = computed(() => {
  const period = `last ${dateRangeDays.value} days`;
  return [
    {
      label: "Alerts sent",
      value: sortedHistories.value.length.toLocaleString(),
      note: localPortal.value?.name ? `${localPortal.value.name}, ${period}` : period,
    },
    { label: "SMS messages", value: totals.value.sms.toLocaleString() },
    { label: "Email messages", value: totals.value.email.toLocaleString() },
    { label: "Push notifications", value: totals.value.push.toLocaleString() },
    {
      label: "Total SMS cost (est.)",
      value: `$${totals.value.cost.toFixed(2)}`,
      title: `Estimated: SMS recipients x message parts x $${SMS_COST_PER_PART} per part`,
    },
    {
      label: "Total email cost (est.)",
      value: formatCost(totals.value.emailCost),
      title: `Estimated: email recipients x $${EMAIL_COST_PER_EMAIL} per email (SES pay-as-you-go, $0.10 per 1,000)`,
    },
  ] as { label: string; value: string; note?: string; title?: string }[];
});

function changeSort(column: SortColumn) {
  if (column === columnToSort.value) {
    isSortingDown.value = !isSortingDown.value;
  } else {
    columnToSort.value = column;
    isSortingDown.value = false;
  }
}

onMounted(() => {
  const saved = alertStore.allHistoriesUiState;
  if (saved && alertStore.allHistories.length > 0) {
    loadedStart.value = saved.loadedStart;
    loadedEnd.value = saved.loadedEnd;
    startDate.value = saved.startDate;
    endDate.value = saved.endDate;
    selectedHistoryId.value = saved.selectedHistoryId;
    selectedPortalId.value = saved.selectedPortalId;
    localPortal.value = bldgStore.properties.find(p => p.portalId === saved.selectedPortalId) ?? null;
    columnToSort.value = saved.columnToSort;
    isSortingDown.value = saved.isSortingDown;
    alertStore.allHistoriesUiState = null;
    nextTick(() => requestAnimationFrame(() => window.scrollTo({ top: saved.scrollY, behavior: "instant" })));
  } else {
    alertStore.allHistoriesUiState = null;
    if (route.query.start) startDate.value = route.query.start as string;
    if (route.query.end) endDate.value = route.query.end as string;
    const queryPortalId = route.query.portalId ? Number(route.query.portalId) : null;
    if (queryPortalId) {
      selectedPortalId.value = queryPortalId;
      const found = bldgStore.properties.find(p => p.portalId === queryPortalId);
      if (found) {
        localPortal.value = found;
      } else {
        const unwatch = watch(() => bldgStore.properties, (props) => {
          if (props.length > 0) {
            localPortal.value = props.find(p => p.portalId === queryPortalId) ?? null;
            unwatch();
          }
        });
      }
    } else {
      selectedPortalId.value = bldgStore.property?.portalId ?? null;
      localPortal.value = bldgStore.property ?? null;
    }
    loadData();
  }
});

function navigateToDetail(alertHistoryId: number) {
  alertStore.allHistoriesUiState = {
    startDate: startDate.value,
    endDate: endDate.value,
    loadedStart: loadedStart.value,
    loadedEnd: loadedEnd.value,
    selectedHistoryId: selectedHistoryId.value,
    selectedPortalId: selectedPortalId.value,
    columnToSort: columnToSort.value,
    isSortingDown: isSortingDown.value,
    scrollY: window.scrollY,
  } satisfies AlertHistoriesUiState;
  router.push(`/staff/alert/detail/${alertHistoryId}`);
}

async function loadData() {
  isLoading.value = true;
  await alertStore.getAllHistory(
    MAX_RESULTS,
    startDate.value || undefined,
    endDate.value || undefined
  );
  loadedStart.value = startDate.value;
  loadedEnd.value = endDate.value;
  isLoading.value = false;
}

const selectedHistoryId = ref(-1);

function selectClick(history: AlertHistoryAllDTO) {
  selectedHistoryId.value =
    history.alertHistoryId === selectedHistoryId.value ? -1 : history.alertHistoryId!;
}
</script>

<style scoped>
/* Native mobile system fonts (iOS / Android / Windows fallback) */
.phone-panel {
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", Roboto, "Segoe UI",
    "Helvetica Neue", Arial, sans-serif;
}

/* Grey "received" SMS bubble with a tail on the bottom-left */
.phone-bubble {
  position: relative;
  display: inline-block;
  max-width: 85%;
  padding: 0.5rem 0.75rem;
  border-radius: 1.1rem;
  background: #e5e5ea;
  color: #000;
  font-size: 0.95rem;
  line-height: 1.3;
  white-space: pre-wrap;
  overflow-wrap: break-word;
}

.phone-bubble::before {
  content: "";
  position: absolute;
  bottom: 0;
  left: -6px;
  width: 14px;
  height: 14px;
  background: #e5e5ea;
  border-bottom-right-radius: 12px;
}

.phone-bubble::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: -10px;
  width: 10px;
  height: 14px;
  background: #f9fafb; /* matches panel bg-gray-50 to carve the tail curve */
  border-bottom-right-radius: 8px;
}
</style>
